import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  findPou,
  type FactorStateKind,
  type NotAssessedReason,
  type Subdomain,
} from '../framework';
import { StatePicker } from './StatePicker';
import { NotAssessedReasonPicker } from './NotAssessedReasonPicker';

type Props = {
  readonly subdomain: Subdomain;
};

/**
 * One row of the framework preview / editor — a single subdomain with its
 * descriptive content (English name, Pou affiliation, Theory of Change
 * outcome) and the StatePicker for recording the current factor state.
 *
 * State is held locally for now — Phase 1 will lift it up to the editor
 * page and wire to a persistence layer once the dev database exists. For
 * now the picker is a working interactive surface that demonstrates the
 * UX without saving anything.
 *
 * When 'not-assessed' is selected, a NotAssessedReasonPicker appears below
 * to capture the practical reason. Clearing the not-assessed state clears
 * the reason too (it's only meaningful in conjunction).
 */
export function SubdomainRow({ subdomain }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<FactorStateKind | null>(null);
  const [reason, setReason] = useState<NotAssessedReason | null>(null);
  const pou = findPou(subdomain.pouId);

  const handleStateChange = (kind: FactorStateKind) => {
    setState(kind);
    if (kind !== 'not-assessed') {
      setReason(null);
    }
  };

  return (
    <li className="subdomain-row">
      <p className="subdomain-row__name">{subdomain.nameEn}</p>
      {pou && (
        <p className="subdomain-row__pou" lang="mi">
          {pou.nameMi}
        </p>
      )}
      <p className="subdomain-row__outcome">
        <span className="subdomain-row__outcome-label">
          {t('preview.tocOutcome')}
        </span>
        <span className="subdomain-row__outcome-text">
          {subdomain.toCOutcome}
        </span>
      </p>

      <StatePicker
        value={state}
        onChange={handleStateChange}
        ariaLabel={t('preview.statePickerLabel', { subdomain: subdomain.nameEn })}
      />

      {state === 'not-assessed' && (
        <NotAssessedReasonPicker
          value={reason}
          onChange={setReason}
          ariaLabel={t('preview.reasonPickerLabel', {
            subdomain: subdomain.nameEn,
          })}
        />
      )}
    </li>
  );
}

export default SubdomainRow;
